<script lang="ts">
  import { onMount } from 'svelte';
  import { ConfigStore } from '../store/config';
  import { state_selectedMode } from '../store/state';
  import type { IModeSwitchOption } from '../interfaces/mode-switch-option.interface';

  let isOpen = false;
  let wrapper: HTMLElement;

  $: options = $ConfigStore.modeSwitchOptions ?? [];

  /** initialise the selected mode from config (default by label, else first item) */
  const initSelectedMode = (): void => {
    if (!options.length) {
      state_selectedMode.set(null);
      return;
    }

    // keep the current selection if it still exists in the options list
    const current = $state_selectedMode;
    if (current && options.some((o) => o.label === current.label)) return;

    const fallback =
      options.find((o) => o.label === $ConfigStore.modeSwitchDefault) ??
      options[0];
    state_selectedMode.set(fallback);
  };

  $: options, initSelectedMode();

  const select = (option: IModeSwitchOption): void => {
    state_selectedMode.set(option);
    isOpen = false;
  };

  const toggle = (): void => {
    isOpen = !isOpen;
  };

  const handleOutsideClick = (e: MouseEvent): void => {
    if (isOpen && wrapper && !wrapper.contains(e.target as Node)) {
      isOpen = false;
    }
  };

  const handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && isOpen) isOpen = false;
  };

  onMount(() => {
    document.addEventListener('click', handleOutsideClick, true);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
      document.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

{#if options.length}
  <div
    class="cs-mode-switch"
    bind:this={wrapper}
  >
    <button
      type="button"
      class="cs-mode-switch__trigger"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      on:click={toggle}
    >
      <span class="cs-mode-switch__label"
        >{$state_selectedMode?.label ?? options[0]?.label}</span
      >
      <span
        class="cs-mode-switch__chevron"
        class:cs-mode-switch__chevron--up={isOpen}
        aria-hidden="true">▾</span
      >
    </button>

    {#if isOpen}
      <ul class="cs-mode-switch__menu" role="listbox">
        {#each options as option (option.label)}
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={$state_selectedMode?.label === option.label}
              class="cs-mode-switch__item"
              class:cs-mode-switch__item--active={$state_selectedMode?.label ===
                option.label}
              on:click={() => select(option)}
            >
              {option.label}
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<style lang="scss">
  .cs-mode-switch {
    // Both input styles anchor this to their positioned .cs-input-wrapper.
    // Keep it below the input box, right-aligned under the submit button
    // (space is reserved by .cs-input-wrapper.cs-has-mode-switch)
    position: absolute;
    top: calc(100% + 5px);
    right: 3px;
    z-index: 15;
    display: inline-flex;
    font-size: 11px;

    &__trigger {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 2px 4px;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: #8a8a96;
      cursor: pointer;
      line-height: 1.2;
      transition:
        background 0.2s,
        color 0.2s;
      max-width: 200px;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: #5a5a66;
      }

      &:focus-visible {
        outline: solid 2px #01012c;
        outline-offset: 1px;
      }
    }

    &__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__chevron {
      font-size: 9px;
      transition: transform 0.2s;

      &--up {
        transform: rotate(180deg);
      }
    }

    &__menu {
      position: absolute;
      bottom: calc(100% + 4px);
      right: 0;
      z-index: 20;
      margin: 0;
      padding: 4px;
      list-style: none;
      min-width: 150px;
      max-width: 240px;
      background: #ffffff;
      border: solid 1px #d4d4dc;
      border-radius: 10px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
    }

    &__item {
      display: block;
      width: 100%;
      text-align: left;
      padding: 7px 10px;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: #2b2b3a;
      cursor: pointer;
      font-size: 12px;
      line-height: 1.3;
      white-space: nowrap;

      &:hover {
        background: #f1f1f4;
      }

      &--active {
        background: #ececf5;
        font-weight: 600;
      }
    }
  }
</style>
